import { useState, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Camera, Upload, X, Tag, MapPin, ImageIcon, Loader2, Search,
  CheckSquare, Pencil, Trash2, Download, CheckCheck,
} from "lucide-react";

import { uploadFileAndGetUrl } from "@/lib/upload";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const PHOTOS_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "photos"];
const SUMMARY_QUERY_KEY = (projectId: number) => ["field", "projects", projectId, "photos", "summary"];

type SitePhoto = {
  id: number;
  projectId: number;
  userId: number;
  url: string;
  area: string | null;
  note: string | null;
  tags: string[];
  takenAt: string | null;
  createdAt: string;
};

type Summary = {
  totalPhotos: number;
  areas: { area: string; count: number }[];
};

async function fetchPhotos(projectId: number): Promise<SitePhoto[]> {
  const res = await fetch(`${BASE}/api/field/projects/${projectId}/photos`);
  if (!res.ok) throw new Error("Failed to fetch photos");
  return res.json();
}

async function fetchSummary(projectId: number): Promise<Summary> {
  const res = await fetch(`${BASE}/api/field/projects/${projectId}/photos/summary`);
  if (!res.ok) throw new Error("Failed to fetch summary");
  return res.json();
}

export default function FieldPhotosTab({ projectId }: { projectId: number }) {
  const { user } = useAuth();
  const isField = user?.role === "field";
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const photosQuery = useQuery({ queryKey: PHOTOS_QUERY_KEY(projectId), queryFn: () => fetchPhotos(projectId) });
  const summaryQuery = useQuery({ queryKey: SUMMARY_QUERY_KEY(projectId), queryFn: () => fetchSummary(projectId) });

  const photos = photosQuery.data ?? [];
  const areas = summaryQuery.data?.areas.map((a) => a.area) ?? [];

  const [activeArea, setActiveArea] = useState("All");
  const [search, setSearch] = useState("");

  // Multi-photo capture state
  type PendingPhoto = { file: File; preview: string };
  type GpsStatus = "idle" | "acquiring" | "ready" | "denied" | "unavailable";
  const [captureOpen, setCaptureOpen] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [captureArea, setCaptureArea] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [gpsStatus, setGpsStatus] = useState<GpsStatus>("idle");
  const [gpsCoords, setGpsCoords] = useState<{ lat: number; lng: number } | null>(null);

  // Bulk select state (owner only)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkArea, setBulkArea] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkTags, setBulkTags] = useState("");
  const [bulkMergeTags, setBulkMergeTags] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const filteredPhotos = photos.filter((p) => {
    const matchesArea =
      activeArea === "All" ||
      (activeArea === "Uncategorized" ? p.area === null : p.area === activeArea);
    const matchesSearch =
      !search ||
      (p.area ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (p.note ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (p.tags ?? []).some((t) => t.toLowerCase().includes(search.toLowerCase()));
    return matchesArea && matchesSearch;
  });

  // ── Select mode helpers ───────────────────────────────────────────────────
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(filteredPhotos.map((p) => p.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const exitSelectMode = () => { setSelectMode(false); clearSelection(); };

  const allSelected = filteredPhotos.length > 0 && filteredPhotos.every((p) => selectedIds.has(p.id));

  // ── Bulk delete ───────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/photos/bulk`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photoIds: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error("Delete failed");
      toast({ title: `Deleted ${selectedIds.size} photo${selectedIds.size === 1 ? "" : "s"}` });
      queryClient.invalidateQueries({ queryKey: PHOTOS_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY(projectId) });
      setConfirmDeleteOpen(false);
      exitSelectMode();
    } catch {
      toast({ title: "Delete failed", variant: "destructive" });
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Bulk export ZIP ───────────────────────────────────────────────────────
  const handleBulkExport = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${BASE}/api/field/projects/${projectId}/photos/bulk-export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photoIds: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `photos-${selectedIds.size}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Export failed", description: "Could not generate ZIP.", variant: "destructive" });
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Bulk edit ─────────────────────────────────────────────────────────────
  const openBulkEdit = () => {
    setBulkArea("");
    setBulkNote("");
    setBulkTags("");
    setBulkMergeTags(false);
    setBulkEditOpen(true);
  };

  const handleBulkEdit = async () => {
    setBulkLoading(true);
    try {
      const body: Record<string, unknown> = {
        photoIds: Array.from(selectedIds),
        mergeTags: bulkMergeTags,
      };
      if (bulkArea.trim()) body.area = bulkArea.trim();
      if (bulkNote.trim()) body.note = bulkNote.trim();
      if (bulkTags.trim()) {
        body.tags = bulkTags.split(",").map((t) => t.trim()).filter(Boolean);
      }

      const res = await fetch(`${BASE}/api/field/projects/${projectId}/photos/bulk`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Edit failed");
      const data = await res.json();
      toast({ title: `Updated ${data.updated} photo${data.updated === 1 ? "" : "s"}` });
      queryClient.invalidateQueries({ queryKey: PHOTOS_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY(projectId) });
      setBulkEditOpen(false);
      exitSelectMode();
    } catch {
      toast({ title: "Bulk edit failed", variant: "destructive" });
    } finally {
      setBulkLoading(false);
    }
  };

  // ── Capture handlers ──────────────────────────────────────────────────────
  const acquireGps = () => {
    if (!navigator.geolocation) { setGpsStatus("unavailable"); return; }
    setGpsStatus("acquiring");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsStatus("ready");
      },
      () => { setGpsStatus("denied"); setGpsCoords(null); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setPendingPhotos((prev) => [...prev, ...next]);
  };

  const removePhoto = (index: number) => {
    setPendingPhotos((prev) => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleCaptureClose = () => {
    if (isUploading) return;
    pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPendingPhotos([]);
    setCaptureArea("");
    setCaptureOpen(false);
    setGpsStatus("idle");
    setGpsCoords(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const handleUploadAll = async () => {
    if (pendingPhotos.length === 0) return;
    setIsUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const photo of pendingPhotos) {
      try {
        const url = await uploadFileAndGetUrl(photo.file);
        const res = await fetch(`${BASE}/api/field/projects/${projectId}/photos`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url,
            area: captureArea || null,
            takenAt: new Date().toISOString(),
            latitude: gpsCoords?.lat ?? null,
            longitude: gpsCoords?.lng ?? null,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { error?: string }).error ?? `Server error ${res.status}`);
        }
        successCount++;
      } catch {
        failCount++;
      }
    }

    if (successCount > 0) {
      queryClient.invalidateQueries({ queryKey: PHOTOS_QUERY_KEY(projectId) });
      queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY(projectId) });
      toast({ title: `${successCount} photo${successCount > 1 ? "s" : ""} added` });
    }
    if (failCount > 0) {
      toast({ title: `${failCount} photo${failCount > 1 ? "s" : ""} failed to upload`, variant: "destructive" });
    }

    pendingPhotos.forEach((p) => URL.revokeObjectURL(p.preview));
    setPendingPhotos([]);
    setCaptureArea("");
    setCaptureOpen(false);
    setIsUploading(false);
    setGpsStatus("idle");
    setGpsCoords(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const allAreas = ["All", ...areas];

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.875rem", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: "160px" }}>
          <Search style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", width: "0.875rem", height: "0.875rem", color: "hsl(var(--muted-foreground))" }} />
          <Input
            placeholder="Search tags, notes, areas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: "2.25rem", height: "2.25rem", fontSize: "0.875rem" }}
          />
        </div>

        {!isField && !selectMode && (
          <>
            <Button size="sm" variant="outline" onClick={() => setSelectMode(true)}>
              <CheckSquare style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
              Select
            </Button>
            <Button size="sm" onClick={() => { setCaptureOpen(true); acquireGps(); }}>
              <Camera style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
              Capture
            </Button>
          </>
        )}

        {!isField && selectMode && (
          <>
            <Button size="sm" variant="outline" onClick={allSelected ? clearSelection : selectAll}>
              <CheckCheck style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
              {allSelected ? "Deselect all" : "Select all"}
            </Button>
            <Button size="sm" variant="outline" onClick={exitSelectMode}>
              Cancel
            </Button>
          </>
        )}
      </div>

      {/* Bulk action bar */}
      {!isField && selectMode && selectedIds.size > 0 && (
        <div style={{
          display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
          padding: "0.625rem 0.875rem", marginBottom: "0.875rem",
          backgroundColor: "hsl(var(--primary) / 0.06)",
          border: "1px solid hsl(var(--primary) / 0.2)",
          borderRadius: "0.5rem",
        }}>
          <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "hsl(var(--primary))", flex: 1 }}>
            {selectedIds.size} selected
          </span>
          <Button size="sm" variant="outline" onClick={openBulkEdit} disabled={bulkLoading}>
            <Pencil style={{ width: "0.8rem", height: "0.8rem", marginRight: "0.3rem" }} />
            Edit
          </Button>
          <Button size="sm" variant="outline" onClick={handleBulkExport} disabled={bulkLoading}>
            {bulkLoading ? <Loader2 style={{ width: "0.8rem", height: "0.8rem", marginRight: "0.3rem" }} className="animate-spin" /> : <Download style={{ width: "0.8rem", height: "0.8rem", marginRight: "0.3rem" }} />}
            Export ZIP
          </Button>
          <Button size="sm" variant="destructive" onClick={() => setConfirmDeleteOpen(true)} disabled={bulkLoading}>
            <Trash2 style={{ width: "0.8rem", height: "0.8rem", marginRight: "0.3rem" }} />
            Delete
          </Button>
        </div>
      )}

      <AlertDialog open={confirmDeleteOpen} onOpenChange={(open) => { if (!bulkLoading) setConfirmDeleteOpen(open); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedIds.size} photo{selectedIds.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The selected photo{selectedIds.size === 1 ? "" : "s"} will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleBulkDelete(); }}
              disabled={bulkLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {bulkLoading ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Area filter chips */}
      {allAreas.length > 1 && (
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem", overflowX: "auto" }}>
          {allAreas.map((area) => (
            <button
              key={area}
              onClick={() => setActiveArea(area)}
              style={{
                padding: "0.25rem 0.875rem",
                borderRadius: "9999px",
                fontSize: "0.8125rem",
                fontWeight: 500,
                cursor: "pointer",
                border: "1px solid",
                transition: "all 0.15s",
                backgroundColor: activeArea === area ? "hsl(var(--primary))" : "transparent",
                borderColor: activeArea === area ? "hsl(var(--primary))" : "hsl(var(--border))",
                color: activeArea === area ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
                whiteSpace: "nowrap",
              }}
            >
              {area}
            </button>
          ))}
        </div>
      )}

      {/* Photo grid */}
      {photosQuery.isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
          <Loader2 style={{ width: "1.5rem", height: "1.5rem" }} className="animate-spin text-muted-foreground" />
        </div>
      ) : filteredPhotos.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "3rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
          <ImageIcon style={{ width: "3rem", height: "3rem", marginBottom: "0.75rem", opacity: 0.3 }} />
          <p style={{ fontWeight: 500 }}>No photos yet</p>
          <p style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>Tap Capture to add your first photo.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "0.75rem" }}>
          {filteredPhotos.map((photo) => {
            const isSelected = selectedIds.has(photo.id);
            return (
              <div
                key={photo.id}
                style={{
                  borderRadius: "0.5rem",
                  overflow: "hidden",
                  backgroundColor: "hsl(var(--card))",
                  border: isSelected
                    ? "2px solid hsl(var(--primary))"
                    : "1px solid hsl(var(--border))",
                  cursor: "pointer",
                  transition: "box-shadow 0.15s, border-color 0.15s",
                  position: "relative",
                }}
                className="hover:shadow-md"
                onClick={() => {
                  if (selectMode) toggleSelect(photo.id);
                  else setLocation(`/field/photo/${photo.id}`);
                }}
              >
                <div style={{ position: "relative", paddingTop: "75%", backgroundColor: "hsl(var(--muted))" }}>
                  <img
                    src={photo.url}
                    alt={photo.area ?? "Site photo"}
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  {photo.area && !selectMode && (
                    <div style={{ position: "absolute", bottom: "0.375rem", left: "0.375rem" }}>
                      <span style={{ backgroundColor: "rgba(0,0,0,0.6)", color: "white", fontSize: "0.7rem", fontWeight: 600, padding: "0.125rem 0.375rem", borderRadius: "0.25rem", display: "flex", alignItems: "center", gap: "0.2rem" }}>
                        <MapPin style={{ width: "0.625rem", height: "0.625rem" }} />
                        {photo.area}
                      </span>
                    </div>
                  )}
                  {/* Select mode checkbox overlay */}
                  {selectMode && (
                    <div style={{
                      position: "absolute", inset: 0,
                      backgroundColor: isSelected ? "hsl(var(--primary) / 0.15)" : "transparent",
                      display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
                      padding: "0.5rem",
                      transition: "background-color 0.15s",
                    }}>
                      <div style={{
                        width: "1.375rem", height: "1.375rem",
                        borderRadius: "0.3rem",
                        backgroundColor: isSelected ? "hsl(var(--primary))" : "white",
                        border: isSelected ? "2px solid hsl(var(--primary))" : "2px solid hsl(var(--border))",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        transition: "all 0.15s",
                      }}>
                        {isSelected && (
                          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M2 5L4.5 7.5L8 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ padding: "0.5rem 0.625rem" }}>
                  <div style={{ fontSize: "0.7rem", color: "hsl(var(--muted-foreground))" }}>
                    {photo.takenAt ? new Date(photo.takenAt).toLocaleDateString() : new Date(photo.createdAt).toLocaleDateString()}
                  </div>
                  {(photo.tags ?? []).length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", marginTop: "0.25rem" }}>
                      {photo.tags.slice(0, 3).map((tag) => (
                        <span key={tag} style={{ fontSize: "0.65rem", backgroundColor: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))", padding: "0.1rem 0.35rem", borderRadius: "0.25rem", display: "flex", alignItems: "center", gap: "0.15rem" }}>
                          <Tag style={{ width: "0.5rem", height: "0.5rem" }} />{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile FAB — hidden in select mode and for field workers */}
      {!isField && !selectMode && (
        <div className="md:hidden" style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 40 }}>
          <Button size="lg" onClick={() => { setCaptureOpen(true); acquireGps(); }} style={{ borderRadius: "9999px", width: "3.5rem", height: "3.5rem", padding: 0, boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}>
            <Camera style={{ width: "1.25rem", height: "1.25rem" }} />
          </Button>
        </div>
      )}

      {/* Hidden file inputs — live outside dialog so they survive dialog unmounts */}
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { addFiles(e.target.files); if (fileInputRef.current) fileInputRef.current.value = ""; }} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          if (cameraInputRef.current) cameraInputRef.current.value = "";
          // Re-opens camera immediately on Android/Chrome; no-op on iOS (user taps "Take Another" instead).
          setTimeout(() => { cameraInputRef.current?.click(); }, 150);
        }} />

      {/* Multi-photo capture dialog */}
      <Dialog open={captureOpen} onOpenChange={(open) => { if (!open) handleCaptureClose(); }}>
        <DialogContent style={{ maxWidth: "32rem" }}>
          <DialogHeader>
            <DialogTitle>Add Photos</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            {pendingPhotos.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))", gap: "0.5rem" }}>
                  {pendingPhotos.map((photo, index) => (
                    <div key={index} style={{ position: "relative", aspectRatio: "1", borderRadius: "0.375rem", overflow: "hidden", backgroundColor: "hsl(var(--muted))" }}>
                      <img src={photo.preview} alt={photo.file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      {!isUploading && (
                        <button
                          onClick={() => removePhoto(index)}
                          style={{ position: "absolute", top: "0.25rem", right: "0.25rem", width: "1.25rem", height: "1.25rem", borderRadius: "9999px", backgroundColor: "rgba(0,0,0,0.6)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                        >
                          <X style={{ width: "0.75rem", height: "0.75rem", color: "white" }} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {!isUploading && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                    <Button variant="secondary" className="w-full h-12 text-base" onClick={() => cameraInputRef.current?.click()}>
                      <Camera style={{ width: "1.1rem", height: "1.1rem", marginRight: "0.5rem" }} />
                      Take Another Photo
                    </Button>
                    <Button variant="ghost" className="w-full" onClick={() => fileInputRef.current?.click()}>
                      <Upload style={{ width: "0.9rem", height: "0.9rem", marginRight: "0.5rem" }} />
                      Add from Library
                    </Button>
                  </div>
                )}
                <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", margin: 0 }}>
                  {pendingPhotos.length} photo{pendingPhotos.length > 1 ? "s" : ""} queued
                </p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.5rem", padding: "1.25rem", border: "2px dashed hsl(var(--border))", borderRadius: "0.5rem", backgroundColor: "transparent", cursor: "pointer", transition: "all 0.15s", fontSize: "0.875rem", fontWeight: 500, color: "hsl(var(--foreground))" }}
                  className="hover:border-primary hover:text-primary hover:bg-primary/5"
                >
                  <Upload style={{ width: "1.5rem", height: "1.5rem" }} />
                  Choose from library
                </button>
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.5rem", padding: "1.25rem", border: "2px dashed hsl(var(--border))", borderRadius: "0.5rem", backgroundColor: "transparent", cursor: "pointer", transition: "all 0.15s", fontSize: "0.875rem", fontWeight: 500, color: "hsl(var(--foreground))" }}
                  className="hover:border-primary hover:text-primary hover:bg-primary/5"
                >
                  <Camera style={{ width: "1.5rem", height: "1.5rem" }} />
                  Take photo
                </button>
              </div>
            )}
            {/* GPS status */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8125rem" }}>
              {gpsStatus === "acquiring" && (
                <>
                  <Loader2 style={{ width: "0.875rem", height: "0.875rem", flexShrink: 0 }} className="animate-spin text-muted-foreground" />
                  <span style={{ color: "hsl(var(--muted-foreground))" }}>Acquiring GPS…</span>
                </>
              )}
              {gpsStatus === "ready" && gpsCoords && (
                <>
                  <span style={{ width: "0.5rem", height: "0.5rem", borderRadius: "9999px", backgroundColor: "#22c55e", flexShrink: 0, display: "inline-block" }} />
                  <a
                    href={`https://maps.google.com/?q=${gpsCoords.lat},${gpsCoords.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "hsl(var(--primary))", textDecoration: "none" }}
                    className="hover:underline"
                  >
                    {gpsCoords.lat.toFixed(5)}, {gpsCoords.lng.toFixed(5)}
                  </a>
                  <span style={{ color: "hsl(var(--muted-foreground))" }}>will be saved with photos</span>
                </>
              )}
              {(gpsStatus === "denied" || gpsStatus === "unavailable") && (
                <>
                  <span style={{ width: "0.5rem", height: "0.5rem", borderRadius: "9999px", backgroundColor: "hsl(var(--muted-foreground))", flexShrink: 0, display: "inline-block" }} />
                  <span style={{ color: "hsl(var(--muted-foreground))" }}>
                    {gpsStatus === "denied" ? "Location access denied — photos will upload without GPS" : "GPS unavailable"}
                  </span>
                </>
              )}
            </div>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Area (optional)</Label>
              <Input
                value={captureArea}
                onChange={(e) => setCaptureArea(e.target.value)}
                placeholder="e.g. Kitchen, Roof, Basement"
                style={{ marginTop: "0.375rem" }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCaptureClose} disabled={isUploading}>Cancel</Button>
            <Button onClick={handleUploadAll} disabled={isUploading || pendingPhotos.length === 0}>
              {isUploading ? (
                <>
                  <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Camera style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
                  Save{" "}
                  {pendingPhotos.length > 0 ? `${pendingPhotos.length} Photo${pendingPhotos.length > 1 ? "s" : ""}` : "Photos"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk edit dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={(open) => { if (!open) setBulkEditOpen(false); }}>
        <DialogContent style={{ maxWidth: "26rem" }}>
          <DialogHeader>
            <DialogTitle>Edit {selectedIds.size} Photo{selectedIds.size === 1 ? "" : "s"}</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem", padding: "0.25rem 0" }}>
            <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
              Leave a field blank to leave it unchanged on the selected photos.
            </p>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Area</Label>
              <Input
                value={bulkArea}
                onChange={(e) => setBulkArea(e.target.value)}
                placeholder="Set area for all selected…"
                style={{ marginTop: "0.375rem" }}
              />
            </div>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Note</Label>
              <Input
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                placeholder="Set note for all selected…"
                style={{ marginTop: "0.375rem" }}
              />
            </div>
            <div>
              <Label style={{ fontSize: "0.8125rem" }}>Tags (comma-separated)</Label>
              <Input
                value={bulkTags}
                onChange={(e) => setBulkTags(e.target.value)}
                placeholder="e.g. defect, warranty, complete"
                style={{ marginTop: "0.375rem" }}
              />
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginTop: "0.5rem" }}>
                <Checkbox
                  id="merge-tags"
                  checked={bulkMergeTags}
                  onCheckedChange={(v) => setBulkMergeTags(!!v)}
                />
                <label htmlFor="merge-tags" style={{ fontSize: "0.8125rem", cursor: "pointer" }}>
                  Merge tags (add to existing, don't replace)
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEditOpen(false)} disabled={bulkLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleBulkEdit}
              disabled={bulkLoading || (!bulkArea.trim() && !bulkNote.trim() && !bulkTags.trim())}
            >
              {bulkLoading && <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" />}
              Apply to {selectedIds.size} Photo{selectedIds.size === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
